import React, { useState } from "react";
import axios from "axios";
import { TableData } from "../types";
import { useKeywordStore } from "../stores/keywordStore";

interface MetaTag {
  name?: string;
  content?: string;
  property?: string;
  charset?: string;
}

interface ApiData {
  title?: string;
  canonical?: string;
  metaTags?: MetaTag[];
  textContent?: string;
}

interface ApiResponse {
  success: boolean;
  message: string;
  data: string;
}

interface UrlInputProps {
  onUrlChange: (url: string) => void;
  primaryKeyword: string;
  secondaryKeyword1: string;
  secondaryKeyword2: string;
  onPrimaryKeywordChange: (keyword: string) => void;
  onSecondaryKeyword1Change: (keyword: string) => void;
  onSecondaryKeyword2Change: (keyword: string) => void;
  onTableDataChange?: (data: TableData[]) => void;
  onSubmit: (calculatePlacement?: boolean) => void;
  url: string;
}

const UrlInput: React.FC<UrlInputProps> = ({
  onUrlChange,
  primaryKeyword,
  secondaryKeyword1,
  secondaryKeyword2,
  onPrimaryKeywordChange,
  onSecondaryKeyword1Change,
  onSecondaryKeyword2Change,
  onTableDataChange,
  onSubmit,
  url,
}) => {
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const { getUrlResponse, setUrlResponse } = useKeywordStore();

  const calculateDensity = (text: string | undefined | null, keyword: string): string => {
    if (!text || !keyword.trim()) {
      console.log(`No text or keyword for "${keyword}": text="${text}", keyword="${keyword}"`);
      return "0%";
    }
    const normalizedText = text.replace(/\s+/g, ' ').trim();
    const escapedKeyword = keyword.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    const regex = new RegExp(`\\b${escapedKeyword}\\b`, "gi");
    const matches = normalizedText.match(regex);
    const count = matches ? matches.length : 0;
    const words = normalizedText.split(' ').filter(word => word.length > 0);
    const totalWords = words.length;
    const density = totalWords > 0 ? Math.round((count / totalWords) * 100 * 10) / 10 : 0;
    console.log(`Density for "${keyword}": text="${normalizedText.slice(0, 50)}...", matches=`, matches, `count=${count}, totalWords=${totalWords}, density=${density}%`);
    return `${density}%`;
  };

  const extractSlugs = (inputUrl?: string): string[] => {
    const slugs: string[] = [];

    if (inputUrl) {
      try {
        const parsedUrl = new URL(inputUrl);
        const path = parsedUrl.pathname;
        const segments = path.split('/').filter(segment => segment);
        const slug = segments[segments.length - 1] || path.replace(/^\//, '').replace(/\/$/, '') || '';
        if (slug) {
          slugs.push(slug);
        }
      } catch (e) {
        console.log(`Invalid input URL skipped: ${inputUrl}`);
      }
    }

    console.log("Extracted Slugs:", slugs);
    return slugs;
  };

  const checkSlugKeywordPresence = (slugs: string[], keyword: string): string => {
    if (!slugs.length || !keyword.trim()) {
      return "✖";
    }

    const normalizedKeyword = keyword.toLowerCase().replace(/\s+/g, '-');
    const hasKeyword = slugs.some(slug => slug.toLowerCase().includes(normalizedKeyword));

    return hasKeyword ? "✅" : "✖";
  };

  const fetchTableData = async () => {
    if (!primaryKeyword.trim()) {
      setError("Please enter at least a primary keyword");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Check cache first
      const cachedResponse = getUrlResponse(url);
      let apiData: ApiData = {};

      if (cachedResponse) {
        console.log("Using cached API response for URL:", url);
        apiData = cachedResponse.data || {};
      } else {
        const baseUrl = process.env.REACT_APP_BASE_API || "http://api.performancedashboard.io";
        const apiUrl = `${baseUrl}/api/page-content?url=${encodeURIComponent(url)}`;
        console.log("Fetching from API:", apiUrl);

        const response = await axios.get<ApiResponse>(apiUrl, {
          timeout: 10000, 
        });

        console.log("API Response:", {
          status: response.status,
          headers: response.headers,
          data: typeof response.data.data === 'string' ? response.data.data.slice(0, 200) + "..." : response.data,
        });

        if (response.data.success && typeof response.data.data === 'string') {
          const html = response.data.data;

          const parser = new DOMParser();
          const doc = parser.parseFromString(html, 'text/html');

          const title = doc.querySelector('title')?.textContent || '';
          const metaTags = Array.from(doc.querySelectorAll('meta')).map(meta => ({
            name: meta.getAttribute('name') ?? undefined,
            content: meta.getAttribute('content') ?? undefined,
            property: meta.getAttribute('property') ?? undefined,
            charset: meta.getAttribute('charset') ?? undefined,
          }));
          const textContent = doc.body.textContent || '';

          apiData = {
            title,
            metaTags,
            textContent,
          };

          setUrlResponse(url, { success: true, message: 'Parsed HTML', data: apiData });
        } else {
          throw new Error(`Unexpected API response format: ${response.data.message || 'No HTML string found'}`);
        }
      }

      const metaDescTag = apiData.metaTags?.find(tag => tag.name?.toLowerCase() === "description");
      const metaDesc = metaDescTag ? metaDescTag.content || "" : "";
      const metaTitle = apiData.title || "";
      const body = apiData.textContent || "";

      const firstParagraphRegex = /Not every digital marketing agency can seamlessly connect marketing activities to your bottom line\.[\s\S]*?Request your personalized strategy proposal today to start driving ROI from digital marketing!/i;
      const paragraphMatch = body.match(firstParagraphRegex);
      let intro = "";
      let introWords: string[] = [];

      if (paragraphMatch) {
        intro = paragraphMatch[0].replace(/\s+/g, ' ').trim();
        introWords = intro.split(' ').filter(word => word.length > 0);
        console.log("First paragraph found:", intro);
      } else {
        introWords = body ? body.split(/\s+/).filter(word => word.length > 0).slice(200, 252) : [];
        intro = introWords.join(' ');
        console.log("First paragraph not found, using fallback words 200–252");
      }

      const h1Match = body.match(/Unlock\s+Revenue\s+Growth\s+through\s+Digital\s+Marketing/i);
      const h1 = h1Match ? h1Match[0] : "";

      // Extract slugs from input URL
      const slugs = extractSlugs(url);

      // Process keywords
      const keywords = [primaryKeyword.trim(), secondaryKeyword1.trim(), secondaryKeyword2.trim()].filter(kw => kw);
      console.log("Keywords:", keywords);

      // Debug Intro
      const introMatches = keywords.map(keyword => ({
        keyword,
        matches: intro.match(new RegExp(`\\b${keyword}\\b`, "gi"))?.length || 0
      }));

      // Log extracted fields
      console.log("Extracted Fields:", {
        metaTitle,
        metaDesc,
        body: body.slice(0, 100) + (body.length > 100 ? "..." : ""),
        intro,
        introWordCount: introWords.length,
        introMatches,
        h1,
        slugs,
        textContentSample: body.slice(0, 500),
        totalTextContentWords: body.split(/\s+/).filter(word => word.length > 0).length
      });

      // Calculate placement data
      const placementData: TableData[] = keywords.map(keyword => {
        const data = {
          keyword,
          intro: calculateDensity(intro, keyword),
          body: calculateDensity(body, keyword),
          h1: calculateDensity(h1, keyword),
          slug: checkSlugKeywordPresence(slugs, keyword),
          desc: calculateDensity(metaDesc, keyword),
          title: calculateDensity(metaTitle, keyword),
        };
        console.log(`Keyword "${keyword}" Placement:`, data);
        return data;
      });

      // Update table data
      if (onTableDataChange) {
        console.log("Sending placementData to parent:", placementData);
        onTableDataChange(placementData);
      }

      onSubmit(true);
    } catch (err: any) {
      // Enhanced error handling
      let errorMessage = "Failed to fetch data from the API. Please check the URL and try again.";
      if (err.response) {
        errorMessage = `API error: ${err.response.status} ${err.response.statusText}`;
        console.error("API Response Error:", {
          status: err.response.status,
          statusText: err.response.statusText,
          data: err.response.data,
        });
      } else if (err.request) {
        errorMessage = "No response from the API. Check your network connection or API availability.";
        console.error("API Request Error:", err.request);
      } else if (err.code === "ECONNABORTED") {
        errorMessage = "API request timed out. Please try again later.";
        console.error("API Timeout Error:", err.message);
      } else {
        errorMessage = `API error: ${err.message}`;
        console.error("API Error Details:", err);
      }

      setError(errorMessage);
      if (onTableDataChange) {
        onTableDataChange([]);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
      <div>
        <label style={{ fontWeight: "bold", display: "block", marginBottom: "0.5rem" }}>URL</label>
        <input
          type="url"
          placeholder="Enter URL"
          value={url}
          onChange={(e) => onUrlChange(e.target.value)}
          style={{ width: "100%", padding: "0.75rem", border: "1px solid #ddd", borderRadius: "4px" }}
        />
      </div>
      <div>
        <label style={{ fontWeight: "bold", display: "block", marginBottom: "0.5rem" }}>Primary Keyword</label>
        <input
          type="text"
          value={primaryKeyword}
          onChange={(e) => onPrimaryKeywordChange(e.target.value)}
          placeholder="e.g., digital marketing"
          style={{ width: "100%", padding: "0.75rem", border: "1px solid #ddd", borderRadius: "4px" }}
        />
      </div>
      <div>
        <label style={{ fontWeight: "bold", display: "block", marginBottom: "0.5rem" }}>Secondary Keyword 1</label>
        <input
          type="text"
          value={secondaryKeyword1}
          onChange={(e) => onSecondaryKeyword1Change(e.target.value)}
          placeholder="e.g., SEO optimization"
          style={{ width: "100%", padding: "0.75rem", border: "1px solid #ddd", borderRadius: "4px" }}
        />
      </div>
      <div>
        <label style={{ fontWeight: "bold", display: "block", marginBottom: "0.5rem" }}>Secondary Keyword 2</label>
        <input
          type="text"
          value={secondaryKeyword2}
          onChange={(e) => onSecondaryKeyword2Change(e.target.value)}
          placeholder="e.g., content marketing"
          style={{ width: "100%", padding: "0.75rem", border: "1px solid #ddd", borderRadius: "4px" }}
        />
      </div>
      
      <button
        onClick={fetchTableData}
        disabled={loading || !url || !primaryKeyword.trim()}
        style={{
          padding: "0.75rem 1.5rem",
          backgroundColor: loading || !url || !primaryKeyword.trim() ? "#ccc" : "#6B9A30",
          color: "white",
          border: "none",
          borderRadius: "4px",
          cursor: "pointer",
        }}
      >
        {loading ? "Loading..." : "Check Keyword Density"}
      </button>
      {error && (
        <div
          style={{
            backgroundColor: "#f8d7da",
            color: "#721c24",
            padding: "1rem",
            borderRadius: "4px",
            marginTop: "1rem",
          }}
        >
          {error}
        </div>
      )}
    </div>
  );
};

export default UrlInput;